/**
 * Viewport diagnostics that ride INSIDE the model subtree — one vocabulary
 * for every door that must ignore them (ADR-006 §8's one-source-of-truth
 * discipline: a second copy of these identities is exactly how the doors
 * drift).
 *
 * ONE IDENTITY FOR EVERYTHING THE VIEWPORT MINTS. Every object the viewport
 * parents into the subject — the weight overlay's heat ghost (ADR-027), the
 * wireframe ghost (ADR-031 T0), the hand markers' discs, rings, halos and
 * vertex clouds (ADR-022) — is stamped, at the moment it is created, into a
 * session-private registry keyed by OBJECT IDENTITY. Never by display name
 * and never by userData: an IMPORTED model's mesh wearing a marker's name is
 * somebody's geometry (Codex P2s, #98 rounds seven and nine), and a GLB's
 * `extras` deserialize into userData, so even a flag there could arrive
 * pre-forged from a file (round ten). Nothing deserializes into a WeakMap.
 *
 * WHY THERE IS NO PREFIX LIST ANY MORE (2026-09-02, the tiger export). The
 * markers used to be recognised by a name prefix — a list of two, of which
 * one (`hand-marker:`) nothing had minted for weeks and the other covered one
 * of the ten families the marker layer actually creates. Fourteen
 * `hand-ghost:*` discs shipped inside a user's file, and the round-trip test
 * was green because it guarded the dead prefix — a door nothing walked. A
 * name is a description; the stamp is the identity. The layer that creates a
 * diagnostic is the only thing that can know it is one, so it says so here.
 *
 * Doors that consult this: exportGlb (a diagnostic must never ship inside a
 * user's file — Codex P1, #98 round one) and soleSkinnedMesh (a ghost must
 * never make the subject read as two meshes and refuse every hand
 * measurement — Codex P1, #98 round six).
 *
 * Depends on: three (types only). Depended on by: export-glb,
 * hands/isolate-hand, and the app's weight-overlay, wireframe-overlay and
 * hand-markers (which stamp what they mint from here).
 */
import type { Object3D } from 'three';

/** What a piece of debris is FOR — the reporting label, never an identity. */
export type ViewportDebrisKind = 'weight-overlay' | 'wireframe' | 'hand-marker' | 'zone-tint';

// Identity NO FILE can forge (Codex P2, #98 round ten): a module-private
// WeakMap holds only objects THIS session marked — nothing deserializes into
// it, and departed objects fall out with the GC.
const sessionDebris = new WeakMap<Object3D, ViewportDebrisKind>();

/** Mark a node THIS session created as one of the viewport's own diagnostics. */
export function markViewportDebris(node: Object3D, kind: ViewportDebrisKind): void {
  sessionDebris.set(node, kind);
}

/** Is this node one of the viewport's own diagnostics (object identity — not name, not userData)? */
export function isViewportDebris(node: Object3D): boolean {
  return sessionDebris.has(node);
}

/**
 * The label reports use for a piece of debris, or null for a non-debris node:
 * its scene-graph name when it has one (markers are named for the panel's
 * sake), else `<kind>-ghost`. Ghosts carry NO name at all — Three's animation
 * binding resolves tracks by name, first match in traversal order, so any
 * name could steal a track from an imported node wearing the same one (Codex
 * P2, #98 round eleven). This is what an export report prints where a name
 * would be.
 */
export function viewportDebrisLabel(node: Object3D): string | null {
  const kind = sessionDebris.get(node);
  if (kind === undefined) return null;
  return node.name !== '' ? node.name : `${kind}-ghost`;
}
